import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Bell, ChevronDown, ChevronUp } from "lucide-react";
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllRead,
  markRead,
} from "@/api/notifications";
import type { Notification } from "@/api/notifications";
import { formatRelativeTime } from "@/lib/formatRelativeTime";
import { translateNotification } from "@/features/notifications/lib/translateNotification";
import { AlertSeverityBadge } from "@/features/admin/lib/badges";
import { Badge } from "@/components/ui/Badge";
import { UnassignedReasonBreakdown } from "@/features/routes/components/UnassignedReasonBreakdown";
import { parseUnassignedPayload } from "@/features/routes/lib/unassignedBreakdown";
import {
  describeBuildWarnings,
  parseBuildWarnings,
} from "@/features/routes/lib/buildWarnings";
import { notificationOutcome } from "@/features/notifications/lib/notificationOutcome";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";

/**
 * Notification bell button with unread badge and dropdown panel.
 *
 * - Displays unread count badge when count > 0.
 * - Clicking the bell opens a dropdown listing the 20 most recent notifications.
 * - Opening the panel does NOT mark anything read (BUG-329): read-state is
 *   per-user, and auto-marking on open zeroed the badge for every dispatcher.
 *   A row is marked read only when the user actually clicks it, or via the
 *   explicit "Mark all read" button.
 * - Clicking outside the panel closes it.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: unreadData } = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: fetchUnreadCount,
    refetchInterval: 60_000,
  });

  const { data: notificationsData } = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: fetchNotifications,
    enabled: open,
  });

  const markAllMutation = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["notifications", "unread-count"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["notifications", "list"],
      });
    },
  });

  const markReadMutation = useMutation({
    mutationFn: markRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["notifications", "unread-count"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["notifications", "list"],
      });
    },
  });

  const unreadCount = unreadData?.count ?? 0;
  const notifications: Notification[] = notificationsData?.results ?? [];

  // Opening the bell must NOT mark anything read (BUG-329) — just toggle.
  function handleBellClick() {
    setOpen((prev) => !prev);
  }

  // Select a notification: mark it read for the current user, then navigate to
  // its deep-link target if it has one. Non-link rows (e.g. broadcasts) are
  // still marked read on click but do not navigate or close the panel.
  function handleSelectNotification(notification: Notification) {
    if (!notification.is_read) {
      markReadMutation.mutate(notification.id);
    }
    const target = notification.link;
    if (target) {
      setOpen(false);
      void navigate(target);
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleMouseDown(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener("mousedown", handleMouseDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      {/* Bell button */}
      <button
        type="button"
        aria-label="Notifications"
        aria-expanded={open}
        onClick={handleBellClick}
        className="relative rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-700"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span
            aria-label={`${unreadCount} unread notifications`}
            className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-0.5 text-[10px] font-semibold leading-none text-white"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-80 rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <p className="text-sm font-semibold text-gray-900">
              {t("notifications.title")}
            </p>
            {unreadCount > 0 ? (
              <button
                type="button"
                onClick={() => markAllMutation.mutate()}
                disabled={markAllMutation.isPending}
                className="text-xs font-medium text-blue-600 transition-colors hover:text-blue-700 disabled:opacity-50"
              >
                {t("notifications.markAllRead")}
              </button>
            ) : null}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex items-center justify-center px-4 py-8">
                <p className="text-sm text-gray-400">
                  {t("notifications.empty")}
                </p>
              </div>
            ) : (
              <TooltipProvider delayDuration={300}>
                <ul>
                  {notifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onSelect={handleSelectNotification}
                    />
                  ))}
                </ul>
              </TooltipProvider>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface NotificationItemProps {
  notification: Notification;
  onSelect: (notification: Notification) => void;
}

function NotificationItem({ notification, onSelect }: NotificationItemProps) {
  // `i18n` is read so the row re-renders (and re-localizes) on a language switch.
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // Every row is now clickable: rows with a link navigate + mark read, rows
  // without one just mark read on click (BUG-329 — read on view/click, not on
  // dropdown open). Unread rows are highlighted.
  const isClickable = Boolean(notification.link) || !notification.is_read;
  // Localize title/body keyed on notification_type, falling back to the raw
  // backend strings for untranslated types (BUG-322). `i18n.language` is
  // referenced to tie the memo to the active language.
  const { title, body } = translateNotification(notification);
  void i18n.language;
  // Broadcast-delivered (system) rows are marked by the backend with
  // `is_system`; `notification_type === "broadcast"` is the equivalent signal
  // for older rows. Either one promotes the row to show the System + severity
  // badges, reusing the shared admin alert-severity color language.
  const isSystem =
    notification.is_system || notification.notification_type === "broadcast";

  // TASK-4741: a route-build notification carries the full unassigned breakdown
  // in `extra_data` (TASK-4740). When present, the row gets an in-panel
  // expandable section naming WHICH orders failed and WHY. The old
  // `line-clamp-2` + hover tooltip clipped the reason inside the 320px panel and
  // hid the detail behind a hover, which is unreachable on touch and invisible to
  // a screen reader — so for these rows the breakdown replaces the clamped body
  // rather than merely supplementing it.
  const unassigned = parseUnassignedPayload(notification.extra_data);
  // BUG-691: a build's warnings (e.g. approximate times) stay on its bell row —
  // the toast goes, this is the record.
  const buildWarnings =
    notification.notification_type === "routes_built"
      ? describeBuildWarnings(
          parseBuildWarnings(notification.extra_data?.warnings),
          t,
        )
      : [];

  // A row reporting dropped orders is a FAILURE and must not read as routine.
  // Distinguished by an icon plus a text badge — never colour alone (WCAG 1.4.1).
  // TASK-4743 lifted this predicate into `notificationOutcome.ts` so the toast
  // raised by `useNotificationsWebSocket` for the SAME event agrees with the row:
  // a second, subtly different copy is how a row reads "Failed" in the bell while
  // its toast pops green.
  // BUG-677: a coded `routes_built` row that created routes but dropped some
  // orders is a PARTIAL success — the toast already shows it as a warning, so the
  // row says «Частично», not «Ошибка». Only "nothing was created" is a failure.
  const outcome = notificationOutcome(notification);
  const isFailure = outcome === "error";
  const isPartialBuild =
    outcome === "warning" && notification.notification_type === "routes_built";

  const content = (
    <>
      {isSystem || isFailure || isPartialBuild ? (
        <div className="mb-1 flex flex-wrap items-center gap-1.5">
          {isSystem ? (
            <Badge variant="planned" label={t("notifications.systemBadge")} />
          ) : null}
          {isFailure ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-danger/10 px-2 py-0.5 text-[11px] font-semibold text-brand-danger">
              <AlertTriangle size={11} aria-hidden="true" />
              {t("notifications.failureBadge")}
            </span>
          ) : null}
          {isPartialBuild ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-warning/15 px-2 py-0.5 text-[11px] font-semibold text-gray-800">
              <AlertTriangle
                size={11}
                className="text-brand-warning"
                aria-hidden="true"
              />
              {t("notifications.partialBadge")}
            </span>
          ) : null}
          {notification.severity ? (
            <AlertSeverityBadge value={notification.severity} />
          ) : null}
        </div>
      ) : null}
      <p
        className={`text-sm leading-snug text-gray-900 ${
          notification.is_read ? "font-normal" : "font-semibold"
        }`}
      >
        {title}
      </p>
      {body ? (
        unassigned ? (
          // Full body, no clamp: this row's whole point is being readable.
          <p className="mt-0.5 text-xs text-gray-500">{body}</p>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">
                {body}
              </p>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{body}</TooltipContent>
          </Tooltip>
        )
      ) : null}
      {buildWarnings.map((warning) => (
        <p
          key={warning}
          data-testid="notification-build-warning"
          className="mt-1 flex items-start gap-1 text-xs text-gray-700"
        >
          <AlertTriangle
            size={12}
            className="mt-0.5 shrink-0 text-brand-warning"
            aria-hidden="true"
          />
          {warning}
        </p>
      ))}
      <p className="mt-1 text-[11px] text-gray-400">
        {formatRelativeTime(notification.created_at)}
      </p>
    </>
  );

  const breakdownId = `notification-breakdown-${notification.id}`;

  return (
    <li
      className={`border-b border-gray-50 last:border-0 ${
        notification.is_read ? "bg-white" : "bg-blue-50"
      }`}
    >
      {isClickable ? (
        <button
          type="button"
          onClick={() => onSelect(notification)}
          className="block w-full cursor-pointer px-4 py-3 text-left outline-none transition-colors hover:bg-gray-50 focus-visible:bg-gray-50"
        >
          {content}
        </button>
      ) : (
        <div className="px-4 py-3">{content}</div>
      )}

      {/*
        The breakdown lives OUTSIDE the row button: nesting a button inside a
        button is invalid HTML, and expanding the detail must not also mark the
        row read and navigate away from the panel.
      */}
      {unassigned ? (
        <div className="px-4 pb-3">
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            aria-controls={breakdownId}
            className="flex items-center gap-1 rounded text-[11px] font-semibold text-gray-700 outline-none transition-colors hover:text-gray-900 focus-visible:underline"
          >
            {expanded ? (
              <ChevronUp size={12} aria-hidden="true" />
            ) : (
              <ChevronDown size={12} aria-hidden="true" />
            )}
            {expanded
              ? t("notifications.hideBreakdown")
              : t("notifications.showBreakdown")}
          </button>

          {expanded ? (
            <div
              id={breakdownId}
              className="mt-2 rounded-lg border border-brand-warning/40 bg-brand-warning/10 px-3 py-2"
            >
              <p className="mb-1.5 text-[11px] font-semibold text-gray-900">
                {t("notifications.unassignedHeading", {
                  count: unassigned.unassigned_total,
                })}
              </p>
              <UnassignedReasonBreakdown payload={unassigned} compact />
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
